import React from 'react';
import { IBorehole } from '../../../@types/model/borehole/borehole';
import { TransitionProps } from '@material-ui/core/transitions/transition';
import Dialog from '@material-ui/core/Dialog';
import { Form } from 'informed';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import boreholeFunctions from '../../../store/borehole/functions';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import DialogContent from '@material-ui/core/DialogContent';
import DivisionSelector from '../../customComponents/selector/DivisionSelector';
import TextField from '@material-ui/core/TextField';
import BoreholeUserSelector from '../../customComponents/selector/borehole/UserSelector';
import FormHelperText from '@material-ui/core/FormHelperText';

interface IEditBoreholeDivisionInfoProps {
    borehole : IBorehole;
    fullWidth ?: boolean;
    maxWidth ?: 'xs' | 'sm' | 'md' | 'lg' | false;
    fullScreen ?: boolean;
    transition ?: React.ForwardRefExoticComponent<TransitionProps & React.RefAttributes<unknown>>;

    isLoading : boolean;
}

interface IEditBoreholeDivisionInfoState {
    open : boolean;
    division : string;
    manArea : string;
    divisionNumber : string;
    accPacDesc : string;
    user : string;
}

export default class EditBoreholeDivisionInfo extends React.Component<IEditBoreholeDivisionInfoProps, IEditBoreholeDivisionInfoState> {
    constructor(props : IEditBoreholeDivisionInfoProps) {
        super(props);
        this.state = {
            open: false,
            division: '',
            manArea: '',
            divisionNumber: '',
            accPacDesc: '',
            user: '',
        };
    }

    public readonly componentDidUpdate = (prevProps : IEditBoreholeDivisionInfoProps, prevState : IEditBoreholeDivisionInfoState) => {
        if (prevState.open && !this.state.open) {
            this.setState({
                division: '',
                manArea: '',
                divisionNumber: '',
                accPacDesc: '',
                user: '',
            });
        }
        if (!prevState.open && this.state.open) {
            this.setState({
                division: !this.props.borehole.Division || this.props.borehole.Division === 'No Data' ? '' : this.props.borehole.Division,
                manArea: !this.props.borehole.ManagementArea ? '' : this.props.borehole.ManagementArea,
                divisionNumber: !this.props.borehole.DivisionNumber ? '' : this.props.borehole.DivisionNumber.toFixed(),
                accPacDesc: !this.props.borehole.DivisionDescriptionAccpac ? '' : this.props.borehole.DivisionDescriptionAccpac,
                user: !this.props.borehole.User ? '' : this.props.borehole.User,
            });
        }
    };

    private readonly onManAreaChanged = (manArea : string) => {
        this.setState({
            manArea,
        });
    };

    private readonly onDivisionChanged = (division : string) => {
        this.setState({
            division,
        });
    };

    private readonly onDivisionNumberChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setState({
            divisionNumber: event.currentTarget.value,
        });
    };

    private readonly onAccPacDescChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        this.setState({
            accPacDesc: event.currentTarget.value,
        });
    };

    private readonly onUserChange = (user : string) => {
        this.setState({
            user,
        });
    };

    private save = async () => {
        const { division, manArea, divisionNumber, accPacDesc, user } = this.state;

        const borehole = Object.assign({}, this.props.borehole);

        borehole.Division = division;
        borehole.ManagementArea = manArea;
        borehole.DivisionNumber = !divisionNumber ? null : parseInt(divisionNumber);
        borehole.DivisionDescriptionAccpac = accPacDesc;
        borehole.User = user;

        await boreholeFunctions.saveBorehole(borehole);
        this.onClose();
    };

    private readonly onSubmit = () => {
        this.save();
    };

    private readonly onEditClick = () => {
        this.setState({
            open: true,
        });
    };

    private readonly onClose = () => {
        if (this.props.isLoading) return;

        this.setState({
            open: false,
        });
    };

    public readonly render = () => {
        const { transition, maxWidth, fullWidth, fullScreen, isLoading } = this.props;
        const { open, division, manArea, divisionNumber, accPacDesc, user } = this.state;
        return (
            <React.Fragment>
                <Tooltip title='Edit'>
                    <div>
                        <IconButton color='inherit' size='small' onClick={this.onEditClick} aria-label='Edit' disabled={isLoading}>
                            <Icon>edit</Icon>
                        </IconButton>
                    </div>
                </Tooltip>
                <Dialog
                    open={open}
                    TransitionComponent={transition}
                    transitionDuration={500}
                    onClose={this.onClose}
                    maxWidth={maxWidth}
                    fullScreen={fullScreen}
                    fullWidth={fullWidth}
                    aria-labelledby='borehole-division-info-title'
                    aria-describedby='borehole-division-info-description'>
                    <AppBar className='fdr posr aic jcc' position='static'>
                        <Toolbar className={'fdr flx1 aic jcc'}>
                            <Typography variant='h5' color='inherit'>
                                Edit Borehole Division Info
                            </Typography>
                            <span className='flx1' />
                            <Tooltip title='Close'>
                                <div>
                                    <IconButton color='inherit' disabled={isLoading} onClick={this.onClose} aria-label='Add'>
                                        <Icon>close</Icon>
                                    </IconButton>
                                </div>
                            </Tooltip>
                        </Toolbar>
                    </AppBar>
                    <Form autoComplete='off' onSubmit={this.onSubmit} className='fdc flx1 hfill'>
                        <DialogContent className='fdc flx1 hfill'>
                            <div className={'fdr flx1 aifs'}>
                                <DivisionSelector division={division} manArea={manArea} onDivisionChanged={this.onDivisionChanged} onManAreaChanged={this.onManAreaChanged} />
                            </div>
                            <div className={'fdr flx1 aifs'}>
                                <div className={'flx1 ais p5 mb10 pr20'}>
                                    <FormControl fullWidth>
                                        <TextField
                                            autoComplete='off'
                                            id='accPacDesc'
                                            label='AccPac Description'
                                            value={accPacDesc}
                                            onChange={this.onAccPacDescChange}
                                            margin='normal'
                                            className={'TextField'}
                                        />
                                    </FormControl>
                                </div>
                                <div className={'flx1 ais p5 mb10 pr20'}>
                                    <FormControl fullWidth>
                                        <TextField
                                            autoComplete='off'
                                            id='divisionNumber'
                                            label='Division Number'
                                            type='number'
                                            value={divisionNumber}
                                            onChange={this.onDivisionNumberChange}
                                            margin='normal'
                                            className={'TextField'}
                                        />
                                        {
                                            divisionNumber && !Number(divisionNumber) &&
                                            <FormHelperText error>Invalid</FormHelperText>
                                        }
                                    </FormControl>
                                </div>
                                <BoreholeUserSelector value={user} onChange={this.onUserChange} />
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button disabled={isLoading} type='submit' variant='contained' color='primary'>
                                OK
                            </Button>
                            <Button disabled={isLoading} variant='outlined' onClick={this.onClose} color='primary' autoFocus>
                                CANCEL
                            </Button>
                        </DialogActions>
                    </Form>
                </Dialog>
            </React.Fragment>);
    };
}
